import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../assets/css/common.css';
import styles from '../assets/css/route-check.module.css';
import { routeService } from '../services/routeService';
import { registrationCheckService } from "../services/registrationCheckService";
import CustomButtons from '../components/CustomButtons';

const COMMUTE_LABELS = { transport: '交通機関利用', walk: '徒歩のみ', remote: 'フルリモート（通勤なし）' };

function RouteCheckConfirm() {
  const location = useLocation();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const {
    routes = [],
    applicant = '',
    address = '',
    commuteType = 'transport',
    busRequired = '',
    walkingImpossible = '',
    totalCost = '',
    attachmentURL = null
  } = location.state || {};

  const hasTransport = commuteType === 'transport';

  useEffect(() => {
    const checkAlreadyRegistered = async () => {
      try {
        const result = await registrationCheckService.checkRegistration();
        if (result?.hasRouteData) {
          navigate('/photo-permission');
        }
      } catch (error) {
        console.error('登録確認エラー:', error);
      } finally {
        setChecking(false);
      }
    };
    checkAlreadyRegistered();
  }, [navigate]);

  const handleBack = () => {
    navigate('/route-check', {
      state: { routes, applicant, address, commuteType, busRequired, walkingImpossible, totalCost, attachmentURL }
    });
  };

  const handleNext = () => {
    if (loading) return;
    setLoading(true);

    const formData = {
      applicant,
      address,
      busRequired: busRequired === 'はい',
      walkingImpossible: walkingImpossible === 'はい',
      totalCost: hasTransport ? totalCost : '0',
      routes: hasTransport ? routes : [],
      attachment: hasTransport ? attachmentURL : null
    };

    routeService.save(formData)
      .then(() => {
        navigate('/photo-permission');
      })
      .catch((error) => {
        console.error('保存エラー:', error);
        setErrorMessage('交通経路情報の保存に失敗しました。もう一度お試しください。');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className={styles.container}>
      <h1 className={`${styles["text-primary"]} text-center mb-4`}>交通経路確認</h1>

      {errorMessage && (
        <div className="alert alert-danger" role="alert">{errorMessage}</div>
      )}

      {/* 基本情報 */}
      <h2 className={styles["section-title"]}>基本情報</h2>
      <table className={styles["details-table"]}>
        <tbody>
          <tr><th>申請者</th><td>{applicant}</td></tr>
          <tr><th>現住所</th><td>{address}</td></tr>
          <tr><th>通勤手段</th><td><strong>{COMMUTE_LABELS[commuteType] || commuteType}</strong></td></tr>
        </tbody>
      </table>

      {hasTransport ? (
        <>
          {/* 交通経路 */}
          <h2 className={styles["section-title"]}>交通経路情報</h2>
          <table className={styles["details-table"]}>
            <thead>
              <tr>
                <th>交通機関名</th><th>発駅</th><th>着駅</th><th>片道料金</th><th>定期代</th>
              </tr>
            </thead>
            <tbody>
              {routes.map((route, index) => (
                <tr key={index}>
                  <td>{route.routeName}</td>
                  <td>{route.fromStation}</td>
                  <td>{route.toStation}</td>
                  <td>{route.oneWayCost}円</td>
                  <td>{route.commuterPassCost}円</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* 利用状況 */}
          <h2 className={styles["section-title"]}>利用状況</h2>
          <p>バス利用: <strong>{busRequired}</strong></p>
          <p>徒歩困難（※直線距離で2kmを超える場合）: <strong>{walkingImpossible}</strong></p>

          {/* 交通費総額 */}
          <h2 className={styles["section-title"]}>交通費総額</h2>
          <p><strong>{totalCost}円</strong></p>

          {/* 添付ファイル */}
          <h2 className={styles["section-title"]}>添付ファイル</h2>
          {attachmentURL ? (
            <img src={attachmentURL} alt="添付画像" style={{ maxWidth: "100%" }} />
          ) : (
            <p>添付ファイルはありません。</p>
          )}
        </>
      ) : (
        <div className="alert alert-info" style={{fontSize:'0.9rem',marginTop:16}}>
          {commuteType === 'walk' ? '徒歩通勤のため、交通経路情報はありません。' : 'フルリモートのため、交通経路情報はありません。'}
        </div>
      )}

      <CustomButtons
        leftLabel="修正する"
        rightLabel="確定して許諾確認へ"
        rightLoadingLabel="保存中..."
        onLeftClick={handleBack}
        onRightClick={handleNext}
        rightLoading={loading}
        rightDisabled={loading}
      />
    </div>
  );
}

export default RouteCheckConfirm;
